import {
  Moon,
  CloudMoon,
  Activity,
  Zap,
  Brain,
  Flame,
  HeartCrack,
} from "lucide-react";

const inputs = [
  { icon: <Moon className="w-4 h-4" />, label: "Sleep Hours" },
  { icon: <CloudMoon className="w-4 h-4" />, label: "Sleep Quality" },
  { icon: <Flame className="w-4 h-4" />, label: "Muscle Readiness" },
  { icon: <Brain className="w-4 h-4" />, label: "Stress" },
  { icon: <Activity className="w-4 h-4" />, label: "Motivation" },
  { icon: <Zap className="w-4 h-4" />, label: "Energy" },
  { icon: <HeartCrack className="w-4 h-4" />, label: "Pain Level" },
];

const recs = [
  {
    color: "#34C759",
    bg: "rgba(52,199,89,0.12)",
    border: "rgba(52,199,89,0.4)",
    label: "Green",
    title: "Train as Planned",
    desc: "Your readiness is high. Execute the session at full intensity.",
  },
  {
    color: "#FFD60A",
    bg: "rgba(255,214,10,0.12)",
    border: "rgba(255,214,10,0.4)",
    label: "Yellow",
    title: "Reduce Volume Slightly",
    desc: "Trim sets or distance. Maintain quality, protect recovery.",
  },
  {
    color: "#FF7A00",
    bg: "rgba(255,122,0,0.12)",
    border: "rgba(255,122,0,0.4)",
    label: "Orange",
    title: "Modify or Switch to Mobility",
    desc: "Swap to a lower-intensity session or a mobility flow.",
  },
  {
    color: "#FF3B30",
    bg: "rgba(255,59,48,0.12)",
    border: "rgba(255,59,48,0.4)",
    label: "Red",
    title: "Recover — Stop If Pain Is High",
    desc: "Prioritize rest. Seek qualified care if pain persists.",
  },
];

export default function NoFatigueScoreSection() {
  return (
    <section
      id="fatigue-score"
      className="relative nfp-section bg-[#0a0a0a] overflow-hidden"
      data-testid="section-fatigue-score"
    >
      <div className="absolute -top-40 right-0 w-[42rem] h-[42rem] rounded-full blur-[140px] bg-[#1FA2FF]/12" />
      <div className="absolute -bottom-40 left-0 w-[36rem] h-[36rem] rounded-full blur-[140px] bg-[#00E5FF]/10" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#1FA2FF] mb-4">
            Signature Feature
          </p>
          <h2 className="nfp-heading text-white text-4xl md:text-6xl leading-[1] tracking-tight">
            The <span className="text-[#00E5FF]">No&nbsp;Fatigue&nbsp;Score</span>
          </h2>
          <p className="mt-5 text-white/65 text-base md:text-lg leading-relaxed">
            Know when to push and when to recover. A daily readiness check-in
            that calibrates every workout — so each session is the right
            session.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 nfp-card p-7 md:p-9 nfp-elite-border bg-[#0c0c0c]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/40">
                  Today's Readiness
                </p>
                <p className="nfp-heading text-7xl md:text-8xl text-white leading-none mt-2">
                  86
                  <span className="text-2xl text-white/40 ml-2">/ 100</span>
                </p>
              </div>
              <span className="px-3 py-1.5 rounded-full text-[11px] font-bold border bg-emerald-500/10 border-emerald-500/40 text-emerald-400">
                ● TRAIN AS PLANNED
              </span>
            </div>

            <div className="mt-6 h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: "86%",
                  background:
                    "linear-gradient(90deg,#FF3B30,#FF7A00,#FFD60A,#34C759)",
                }}
              />
            </div>

            <p className="mt-6 text-[10px] uppercase tracking-[0.25em] text-white/40">
              Daily Check-in
            </p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {inputs.map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
                >
                  <div className="flex items-center gap-2 text-[#00E5FF]">
                    {s.icon}
                    <span className="text-[10px] uppercase tracking-[0.15em] text-white/55">
                      {s.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-3">
            {recs.map((r) => (
              <div
                key={r.label}
                data-testid={`rec-${r.label.toLowerCase()}`}
                className="rounded-2xl border p-5 flex items-start gap-4 transition-all hover:-translate-y-0.5"
                style={{ background: r.bg, borderColor: r.border }}
              >
                <span
                  className="w-3 h-3 mt-1.5 rounded-full shrink-0"
                  style={{ background: r.color, boxShadow: `0 0 12px ${r.color}` }}
                />
                <div>
                  <span
                    className="text-[10px] uppercase tracking-[0.25em] font-bold"
                    style={{ color: r.color }}
                  >
                    {r.label}
                  </span>
                  <h3 className="text-white text-lg font-semibold mt-1">{r.title}</h3>
                  <p className="text-sm text-white/65 mt-1 leading-relaxed">{r.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
